import {
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { AnalyseForm } from "@/components/FormEngine/components/AnalyseForm/component";
import { FormRenderer } from "@/components/FormEngine/components/FormRenderer/component";
import { JsonInputPanel } from "@/components/FormEngine/components/JsonInput/component";
import { JsonViewer } from "@/components/FormEngine/components/JsonViewer/component";
import { SnackbarComponent } from "@/components/FormEngine/components/SnackBar/component";
import { TabPanel } from "@/components/FormEngine/components/TabPanel/component";
import { TabsIndex } from "@/components/FormEngine/components/TabPanel/types";
import { FE_STRINGS } from "@/components/FormEngine/constants";
import { useClipboard, useSnackbar } from "@/components/FormEngine/hooks";
import { useFormEngine } from "@/components/FormEngine/hooks/useFormEngine";

import {
  StyledGeneratedFormPaper,
  StyledHeaderPaper,
  StyledLoadingContainer,
  StyledPageContainer,
  StyledTabPanelContainer,
  StyledTabsContainer,
  StyledTitle,
} from "./styles";

const FormEngine = () => {
  const {
    initialForm,
    setInitialForm,
    isLoading,
    error,
    parsedForm,
    handleLoadFromFile,
    handleApplyJson,
    transformedJson,
    anomaliesCount,
    fieldsMap,
    applyPathBasedChange,
  } = useFormEngine();

  const [activeTab, setActiveTab] = useState<number>(TabsIndex.FormRenderer);
  const { copyText } = useClipboard();
  const { snackbar, show, close } = useSnackbar();

  const onTabChange = useCallback(
    (_evt: React.SyntheticEvent, v: number) => setActiveTab(v),
    [],
  );

  const onCopyJson = useCallback(async () => {
    const ok = await copyText(transformedJson);
    show(
      ok ? FE_STRINGS.messages.copyOk : FE_STRINGS.messages.copyFail,
      ok ? "success" : "error",
    );
  }, [copyText, transformedJson, show]);

  // NEW: sérialisation live du flat map pour visualiser les changements
  const flatMapJson = useMemo(
    () => JSON.stringify(fieldsMap, null, 2),
    [fieldsMap],
  );

  const onCopyFlatMap = useCallback(async () => {
    const ok = await copyText(flatMapJson);
    show(
      ok ? FE_STRINGS.messages.copyOk : FE_STRINGS.messages.copyFail,
      ok ? "success" : "error",
    );
  }, [copyText, flatMapJson, show]);

  if (isLoading) {
    return (
      <StyledLoadingContainer>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{FE_STRINGS.loading.label}</Typography>
      </StyledLoadingContainer>
    );
  }

  return (
    <StyledPageContainer>
      <StyledHeaderPaper elevation={2}>
        <StyledTitle variant='title3'>{FE_STRINGS.header.title}</StyledTitle>
      </StyledHeaderPaper>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <JsonInputPanel
            raw={initialForm}
            setRaw={setInitialForm}
            applyJson={handleApplyJson}
            loadFromFile={handleLoadFromFile}
            error={error}
          />
        </Grid>

        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <StyledGeneratedFormPaper
            elevation={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <StyledTabsContainer>
              <Tabs value={activeTab} onChange={onTabChange}>
                <Tab label={FE_STRINGS.tabs.interactive} />
                <Tab
                  label={
                    <span style={{ display: "flex", alignItems: "center" }}>
                      {FE_STRINGS.tabs.analyse}
                      <Chip
                        label={anomaliesCount}
                        size='small'
                        color={anomaliesCount > 0 ? "error" : "default"}
                        sx={{ ml: 1 }}
                      />
                    </span>
                  }
                />
                <Tab label={FE_STRINGS.tabs.json} />
                {/* NEW: renommer le dernier onglet pour refléter le flat map */}
                <Tab label='Flat map' />
              </Tabs>
            </StyledTabsContainer>

            <StyledTabPanelContainer
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <TabPanel value={activeTab} index={TabsIndex.Analyse}>
                <AnalyseForm analysedForm={parsedForm} />
              </TabPanel>

              <TabPanel value={activeTab} index={TabsIndex.FormRenderer}>
                {parsedForm ? (
                  <FormRenderer
                    parsedForm={parsedForm}
                    fieldsMap={fieldsMap}
                    onValueChange={(_id, path, next, impacts) => {
                      applyPathBasedChange(path, next, impacts);
                    }}
                  />
                ) : (
                  <Typography color='text.secondary' sx={{ p: 2 }}>
                    {FE_STRINGS.messages.interactiveHint}
                  </Typography>
                )}
              </TabPanel>

              <TabPanel value={activeTab} index={TabsIndex.Json}>
                <JsonViewer value={transformedJson} onCopy={onCopyJson} />
              </TabPanel>

              {/* NEW: afficher le flat map live dans le dernier tab */}
              <TabPanel value={activeTab} index={TabsIndex.Output}>
                <JsonViewer value={flatMapJson} onCopy={onCopyFlatMap} />
              </TabPanel>
            </StyledTabPanelContainer>
          </StyledGeneratedFormPaper>
        </Grid>
      </Grid>

      <SnackbarComponent state={snackbar} onClose={close} />
    </StyledPageContainer>
  );
};

export default FormEngine;
